import * as Sentry from '@sentry/react';
import type { Mixpanel } from 'mixpanel-browser';

import type {
  MixpanelEventName,
  MixpanelEventProperties,
} from './mixpanelEvents';

export interface MixpanelActiveClient {
  id: number;
  name: string;
}

export interface MixpanelEventContext {
  activeClient: MixpanelActiveClient | null;
  resetVersion: number;
}

interface MixpanelPageViewProperties {
  active_client_id: number;
  active_client_name: string;
  current_url_hash: string;
  current_url_path: string;
  current_url_search: string;
}

interface MixpanelUserProperties {
  clientIds: number[];
  clientNames: string[];
  email: string | null;
  isRsiEmployee: boolean;
  isUserAdmin: boolean;
}

interface IdentifiedUser extends MixpanelUserProperties {
  id: string;
}

let mixpanelInstance: Mixpanel | null = null;
let mixpanelInitialization: Promise<Mixpanel | null> | null = null;
let activeClient: MixpanelActiveClient | null = null;
let identifiedUser: IdentifiedUser | null = null;
/* oxlint-disable jest/require-hook -- These values are runtime Mixpanel state, not test setup. */
const pendingPageViews: MixpanelPageViewProperties[] = [];
let resetVersion = 0;
/* oxlint-enable jest/require-hook */

const isMixpanelConfigured =
  import.meta.env.VITE_ANALYTICS_DISABLED !== 'true' &&
  !!import.meta.env.VITE_MIXPANEL_TOKEN;

function registerBaseProperties(mixpanel: Mixpanel) {
  mixpanel.register({
    environment: import.meta.env.VITE_ENVIRONMENT,
    platform: 'web',
  });
}

function setUserProfile(mixpanel: Mixpanel, user: IdentifiedUser) {
  mixpanel.identify(user.id);
  mixpanel.people.set({
    $email: user.email,
    client_ids: user.clientIds,
    client_names: user.clientNames,
    is_rsi_employee: user.isRsiEmployee,
    is_user_admin: user.isUserAdmin,
  });
}

function flushPageView(mixpanel: Mixpanel) {
  if (!pendingPageViews.length || !identifiedUser || !activeClient) {
    return;
  }

  const pageViews = pendingPageViews.splice(0);
  for (const properties of pageViews) {
    mixpanel.track_pageview(properties);
  }
}

// oxlint-disable-next-line typescript/promise-function-async
function getMixpanel(): Promise<Mixpanel | null> {
  if (!isMixpanelConfigured) {
    return Promise.resolve(null);
  }

  if (mixpanelInstance) {
    return Promise.resolve(mixpanelInstance);
  }

  const initializationResetVersion = resetVersion;
  mixpanelInitialization ??= import('mixpanel-browser')
    .then(({ default: mixpanel }) => {
      const token = import.meta.env.VITE_MIXPANEL_TOKEN;
      if (!token) {
        return null;
      }

      mixpanel.init(token, {
        persistence: 'localStorage',
        track_pageview: false,
      });
      if (initializationResetVersion !== resetVersion) {
        mixpanel.reset();
      }

      registerBaseProperties(mixpanel);
      if (identifiedUser) {
        setUserProfile(mixpanel, identifiedUser);
      }
      if (activeClient) {
        mixpanel.register({
          active_client_id: activeClient.id,
          active_client_name: activeClient.name,
        });
      }

      mixpanelInstance = mixpanel;
      flushPageView(mixpanel);
      return mixpanel;
    })
    .catch((error: unknown) => {
      mixpanelInitialization = null;
      pendingPageViews.length = 0;
      Sentry.captureException(error);
      return null;
    });

  return mixpanelInitialization;
}

export function identifyMixpanelUser(
  id: number,
  properties: MixpanelUserProperties
) {
  identifiedUser = { id: String(id), ...properties };

  // oxlint-disable-next-line eslint/no-void
  void getMixpanel().then((mixpanel) => {
    if (mixpanel && identifiedUser) {
      setUserProfile(mixpanel, identifiedUser);
      flushPageView(mixpanel);
    }

    return mixpanel;
  });
}

export function setMixpanelActiveClient(id: number, name: string) {
  activeClient = { id, name };

  // oxlint-disable-next-line eslint/no-void
  void getMixpanel().then((mixpanel) => {
    if (mixpanel && activeClient) {
      mixpanel.register({
        active_client_id: activeClient.id,
        active_client_name: activeClient.name,
      });
      flushPageView(mixpanel);
    }

    return mixpanel;
  });
}

export function clearMixpanelActiveClient() {
  activeClient = null;
  mixpanelInstance?.unregister('active_client_id');
  mixpanelInstance?.unregister('active_client_name');
}

export function getMixpanelActiveClient() {
  return activeClient ? { ...activeClient } : null;
}

export function getMixpanelEventContext(
  eventClient: MixpanelActiveClient | null = activeClient
): MixpanelEventContext {
  return {
    activeClient: eventClient ? { ...eventClient } : null,
    resetVersion,
  };
}

export function resetMixpanelUser() {
  resetVersion += 1;
  activeClient = null;
  identifiedUser = null;
  pendingPageViews.length = 0;
  mixpanelInstance?.reset();
  if (mixpanelInstance) {
    registerBaseProperties(mixpanelInstance);
  }
}

export function trackMixpanelPageView(properties: MixpanelPageViewProperties) {
  if (!isMixpanelConfigured) {
    return;
  }

  pendingPageViews.push(properties);

  // oxlint-disable-next-line eslint/no-void
  void getMixpanel().then((mixpanel) => {
    if (mixpanel) {
      flushPageView(mixpanel);
    }

    return mixpanel;
  });
}

export function trackMixpanelEvent<TEventName extends MixpanelEventName>(
  name: TEventName,
  properties: MixpanelEventProperties[TEventName],
  context = getMixpanelEventContext()
) {
  // oxlint-disable-next-line eslint/no-void
  void getMixpanel().then((mixpanel) => {
    if (!mixpanel || context.resetVersion !== resetVersion) {
      return mixpanel;
    }
    if (context.activeClient) {
      mixpanel.register({
        active_client_id: context.activeClient.id,
        active_client_name: context.activeClient.name,
      });
    }
    mixpanel.track(name, properties);
    return mixpanel;
  });
}
